import { useCallback, useEffect, useMemo, useState } from "react";
import { Bookmark, BookmarkPlus, Trash2 } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Input, cn } from "@ncp/ui";

export interface SavedView<TState> {
  id: string;
  name: string;
  state: TState;
  createdAt: string;
}

/**
 * Per-table saved views (filters, sort, visible columns) kept in local storage under `ncp.admin.views.<tableKey>`.
 * Views are personal to the browser profile; a later release can move them to the API.
 */
export function useSavedViews<TState>(tableKey: string) {
  const storageKey = `ncp.admin.views.${tableKey}`;
  const [views, setViews] = useState<SavedView<TState>[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? "[]") as SavedView<TState>[];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(views));
    } catch {
      /* storage unavailable */
    }
  }, [storageKey, views]);

  const save = useCallback((name: string, state: TState) => {
    setViews((v) => [...v.filter((x) => x.name !== name), { id: crypto.randomUUID(), name, state, createdAt: new Date().toISOString() }]);
  }, []);
  const remove = useCallback((id: string) => setViews((v) => v.filter((x) => x.id !== id)), []);
  return useMemo(() => ({ views, save, remove }), [views, save, remove]);
}

export function SavedViewsMenu<TState>({ tableKey, current, onApply, className }: { tableKey: string; current: TState; onApply: (state: TState) => void; className?: string }) {
  const { t } = useTranslation();
  const { views, save, remove } = useSavedViews<TState>(tableKey);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Bookmark /> {t("admin:shell.savedViews")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          <DropdownMenuLabel>{t("admin:shell.savedViews")}</DropdownMenuLabel>
          {views.length === 0 ? <p className="px-2 py-1.5 text-xs text-muted-foreground">{t("admin:shell.noSavedViews")}</p> : null}
          {views.map((v) => (
            <DropdownMenuItem key={v.id} onSelect={() => onApply(v.state)} className="group flex items-center justify-between gap-2">
              <span className="truncate">{v.name}</span>
              <button
                type="button"
                aria-label={t("admin:shell.deleteView")}
                className="rounded p-0.5 text-sand-500 opacity-0 transition-opacity hover:text-danger-600 group-hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  remove(v.id);
                }}
              >
                <Trash2 className="size-3.5" />
              </button>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setNaming(true)}>
            <BookmarkPlus className="size-4" /> {t("admin:shell.saveView")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {naming ? (
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) save(name.trim(), current);
            setName("");
            setNaming(false);
          }}
        >
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin:shell.viewName")} className="h-8 w-40 text-xs" />
          <Button type="submit" size="sm">{t("admin:common.save")}</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setNaming(false)}>{t("admin:common.cancel")}</Button>
        </form>
      ) : null}
    </div>
  );
}
